import { RecipientInput } from './recipient-input';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import { useState, useEffect, useRef, useCallback, type Dispatch, type SetStateAction } from 'react';
import { Bold, Italic, List, Link as LinkIcon, Paperclip, X, FileEdit, Check } from 'lucide-react';
import { ImageOptimizeModal, isOptimizableImage } from './image-optimize-modal';
import { LinkEditModal, type LinkEditResult } from './link-edit-modal';
import { guessTypeFromName, planAttachment, fitsAfterConversion, READ_CEILING_MULTIPLE } from '@/lib/image-optimize-rules';
import { useUi } from './ui-dialogs';

export interface Attachment {
  filename: string;
  contentType: string;
  content: string; // base64
  size: number;
}

// Attachments ride along as base64 inside the JSON send body, so they inflate ~4/3 on
// the wire. The backend allows a 32 MiB body; 20 MB of raw files leaves headroom for
// the encoding overhead and the message itself. Guarding here means the user gets a
// real message at attach time instead of a 413 after writing the whole email.
export const MAX_TOTAL_ATTACHMENT_BYTES = 20 * 1024 * 1024;

// Accept what people actually type ("briannunes.com") and turn it into something a mail
// client will follow, while refusing schemes that execute rather than navigate.
function normalizeUrl(input: string): string | null {
  const raw = input.trim();
  if (!raw) return null;
  if (/^(javascript|data|vbscript|file):/i.test(raw)) return null;
  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(raw) ? raw : `https://${raw}`;
  try {
    const url = new URL(withScheme);
    if (!['http:', 'https:', 'mailto:', 'tel:'].includes(url.protocol)) return null;
    return url.toString();
  } catch {
    return null;
  }
}

interface ComposeEditorProps {
  to: string;
  setTo: (v: string) => void;
  cc: string;
  setCc: (v: string) => void;
  bcc: string;
  setBcc: (v: string) => void;
  subject: string;
  setSubject: (v: string) => void;
  body: string;
  onBodyChange: (html: string) => void;
  onSend: () => void;
  onSaveDraft: () => void;
  draftSaving: boolean;
  draftSaved: boolean;
  isSending: boolean;
  attachments: Attachment[];
  setAttachments: Dispatch<SetStateAction<Attachment[]>>;
}

export function ComposeEditor({ to, setTo, cc, setCc, bcc, setBcc, subject, setSubject, body, onBodyChange, onSend, onSaveDraft, draftSaving, draftSaved, isSending, attachments, setAttachments }: ComposeEditorProps) {
  const ui = useUi();
  const editor = useEditor({
    extensions: [StarterKit, Link],
    content: body,
    onUpdate: ({ editor }) => onBodyChange(editor.getHTML()),
    editorProps: {
      attributes: {
        class: 'prose prose-invert max-w-none min-h-[12rem] px-4 py-3 outline-none text-bmail-text'
      }
    }
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  // 🚩 [Brian 2026-10-01] The optimise offer. Held as one object so the modal is either open with a
  // complete set of files or not open at all — a half-filled offer would attach half a selection.
  const [optimizeOffer, setOptimizeOffer] = useState<
    { images: Attachment[]; passthrough: Attachment[]; required: boolean } | null>(null);
  // [Brian 2026-10-30] The link editor: two fields (title + URL) instead of the old single-URL
  // prompt. Prefill is computed at open time so the modal is either closed or holds both fields.
  const [linkEdit, setLinkEdit] = useState<{ href: string; title: string } | null>(null);

  const readAsBase64 = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      // result is "data:<mime>;base64,<data>" — strip the prefix
      reader.onload = () => resolve((reader.result as string).split(',')[1] || '');
      reader.onerror = () => reject(reader.error ?? new Error(`Could not read ${file.name}`));
      reader.readAsDataURL(file);
    });

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    // Reset the input immediately so picking the same file again still fires onChange.
    e.target.value = '';
    if (files.length === 0) return;

    const currentBytes = attachments.reduce((sum, a) => sum + a.size, 0);
    const incomingBytes = files.reduce((sum, f) => sum + f.size, 0);
    // The decision itself lives in lib/ so it can be unit-tested; see `planAttachment` for why an
    // over-cap selection is now an offer rather than a refusal.
    const plan = planAttachment(
      currentBytes,
      files.map((f) => ({ contentType: f.type || guessTypeFromName(f.name), size: f.size })),
      MAX_TOTAL_ATTACHMENT_BYTES,
    );
    const mb = (n: number) => Math.round(n / 1024 / 1024);
    if (plan.action === 'reject') {
      ui.toast(plan.reason === 'too-much-to-read'
        ? {
            kind: 'error',
            message: 'That is too much to read at once',
            detail: `${mb(incomingBytes)} MB selected, and the most this will take in one go is `
              + `${mb(READ_CEILING_MULTIPLE * MAX_TOTAL_ATTACHMENT_BYTES)} MB. Attach them in smaller batches.`,
          }
        : {
            kind: 'error',
            message: 'Attachments are too large',
            detail: `Limit is ${mb(MAX_TOTAL_ATTACHMENT_BYTES)} MB total; that selection would make `
              + `${mb(currentBytes + incomingBytes)} MB, and none of it is an image that can be shrunk.`,
          });
      return;
    }

    // Read every file before touching state, then append once. Reading them one-by-one
    // and appending per-file raced: each FileReader callback closed over the same
    // `attachments` snapshot, so selecting N files kept only whichever finished last.
    try {
      const read = await Promise.all(
        files.map(async (file) => ({
          filename: file.name,
          // 🔴 Chrome on Linux/Windows reports an EMPTY type for .heic — there is no registered MIME
          // — so a dragged iPhone photo would never match the image test and the HEIC bridge would
          // never be reached on the one platform that actually hands over HEIC (iOS Safari
          // transcodes to JPEG on upload). Fall back to the extension.
          contentType: file.type || guessTypeFromName(file.name),
          content: await readAsBase64(file),
          size: file.size
        }))
      );
      // 🔴 Images big enough to be worth it get the offer; everything else attaches straight away.
      // The offer is OPTIONAL by Brian's brief, so the modal's own "Attach as they are" and its close
      // button both keep the originals — nothing is converted without someone saying so.
      const images = read.filter(isOptimizableImage);
      if (images.length > 0) {
        const passthrough = read.filter((a) => !isOptimizableImage(a));
        // `required` when the originals would not fit: converting is then the only way these attach,
        // so the modal drops "Attach as they are" rather than offering a button that cannot work.
        setOptimizeOffer({ images, passthrough, required: plan.action === 'offer' && plan.required });
        return;
      }
      setAttachments((prev) => [...prev, ...read]);
    } catch (err) {
      ui.toast({ kind: 'error', message: 'Could not attach those files', detail: (err as Error).message });
    }
  }, [attachments, setAttachments, ui]);

  const removeAttachment = useCallback((index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  }, [setAttachments]);

  // [Brian 2026-10-30] "the hyperlink editor should have a field for a title and a link" — the
  // old single prompt could only ever show the address as the text. The button now opens a
  // two-field modal; the selection (or the whole existing link) prefill the title.
  const applyLink = useCallback(() => {
    if (!editor) return;
    const existing = editor.getAttributes('link').href as string | undefined;
    let title = editor.state.doc.textBetween(editor.state.selection.from, editor.state.selection.to, ' ');
    if (existing) {
      // Editing: widen to the whole link first, so the title field reads its full text
      // instead of whatever slice the caret sat in.
      editor.chain().focus().extendMarkRange('link').run();
      title = editor.state.doc.textBetween(editor.state.selection.from, editor.state.selection.to, ' ');
    }
    setLinkEdit({ href: existing ?? '', title });
  }, [editor]);

  const saveLink = useCallback((r: LinkEditResult) => {
    if (!editor) return;
    const href = normalizeUrl(r.href);
    if (!href) {
      ui.toast({ kind: 'error', message: 'That doesn’t look like a link', detail: 'Try something like https://example.com' });
      return; // keep the modal open with what was typed — the fix is a keystroke away
    }
    // A title replaces the selection (or the whole link being edited, already extended at
    // open time) with that text, linked. No title keeps the old behavior: with a selection the
    // selected text becomes the link; with a bare cursor the address itself is inserted as
    // linked text — setLink alone would only arm a stored mark that the next keystroke
    // consumed somewhere unexpected (the same trap the old comment named).
    const titled = r.title.trim();
    if (titled) {
      editor.chain().focus().insertContent({ type: 'text', text: titled, marks: [{ type: 'link', attrs: { href } }] }).run();
    } else if (editor.state.selection.empty && !editor.isActive('link')) {
      editor.chain().focus().insertContent({ type: 'text', text: href, marks: [{ type: 'link', attrs: { href } }] }).run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
    }
    setLinkEdit(null);
  }, [editor, ui]);

  // The reply/forward quote is set on `body` asynchronously (after the original
  // message loads), but useEditor only reads `content` at mount. Push external
  // `body` changes into the editor so the quoted thread actually shows (and gets
  // sent). `false` = don't emit onUpdate, so the user's own typing won't loop.
  const lastApplied = useRef<string | null>(null);
  useEffect(() => {
    if (!editor) return;
    if (body !== lastApplied.current) {
      lastApplied.current = body;
      if (editor.getHTML() !== body) editor.commands.setContent(body || '', false);
    }
  }, [body, editor]);

  return (
    <div className="flex h-full flex-col">
      <div className="space-y-3 px-6 py-4">
        <div className="flex items-center gap-2">
          <span className="w-10 text-sm font-medium text-bmail-text-secondary">To:</span>
          <RecipientInput value={to} onChange={setTo} />
        </div>
        <div className="flex items-center gap-2">
          <span className="w-10 text-sm font-medium text-bmail-text-secondary">Cc:</span>
          <RecipientInput label="Cc" value={cc} onChange={setCc} />
        </div>
        <div className="flex items-center gap-2">
          <span className="w-10 text-sm font-medium text-bmail-text-secondary">Bcc:</span>
          <RecipientInput label="Bcc" value={bcc} onChange={setBcc} />
        </div>
        <div className="flex items-center gap-2">
          <span className="w-10 text-sm font-medium text-bmail-text-secondary">Sub:</span>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Subject"
            className="flex-1 rounded-lg border border-bmail-border bg-bmail-navy px-3 py-2 text-sm text-bmail-text placeholder-slate-500 focus:border-bmail-gold focus:outline-none"
          />
        </div>
      </div>

      <div className="flex items-center gap-1 border-y border-bmail-border bg-bmail-surface px-4 py-1">
        <button
          onClick={() => editor?.chain().focus().toggleBold().run()}
          className={`rounded p-2 ${editor?.isActive('bold') ? 'text-bmail-gold' : 'text-bmail-text hover:text-bmail-text'}`}
        >
          <Bold size={16} />
        </button>
        <button
          onClick={() => editor?.chain().focus().toggleItalic().run()}
          className={`rounded p-2 ${editor?.isActive('italic') ? 'text-bmail-gold' : 'text-bmail-text hover:text-bmail-text'}`}
        >
          <Italic size={16} />
        </button>
        <button
          onClick={() => editor?.chain().focus().toggleBulletList().run()}
          className={`rounded p-2 ${editor?.isActive('bulletList') ? 'text-bmail-gold' : 'text-bmail-text hover:text-bmail-text'}`}
        >
          <List size={16} />
        </button>
        <button
          onClick={applyLink}
          title="Insert link"
          className={`rounded p-2 ${editor?.isActive('link') ? 'text-bmail-gold' : 'text-bmail-text hover:text-bmail-text'}`}
        >
          <LinkIcon size={16} />
        </button>
        <button
          onClick={() => fileInputRef.current?.click()}
          className="rounded p-2 text-bmail-text hover:text-bmail-text"
          title="Attach files"
        >
          <Paperclip size={16} />
        </button>
        {optimizeOffer && (
          <ImageOptimizeModal
            images={optimizeOffer.images}
            passthrough={optimizeOffer.passthrough}
            required={optimizeOffer.required}
            onDone={(all, notes) => {
              setOptimizeOffer(null);
              // 🔴 THE CAP IS JUDGED HERE, on what will actually be sent. This is the whole point of
              // converting first: 39 MB of photos that come back as 4 MB are attachable, and the old
              // code refused them on their original size before conversion ever ran.
              const already = attachments.reduce((sum, a) => sum + a.size, 0);
              const adding = all.reduce((sum, a) => sum + a.size, 0);
              if (!fitsAfterConversion(already, all, MAX_TOTAL_ATTACHMENT_BYTES)) {
                // 🔒 Nothing is attached. Attaching "as much as fits" would silently drop files the
                // user picked, which is worse than saying it did not work.
                ui.toast({
                  kind: 'error',
                  message: 'Still too large after converting',
                  detail: `${Math.round(adding / 1024 / 1024)} MB after conversion, and the limit is `
                    + `${Math.round(MAX_TOTAL_ATTACHMENT_BYTES / 1024 / 1024)} MB total`
                    + `${already ? ` (${Math.round(already / 1024 / 1024)} MB is already attached)` : ''}`
                    + '. Try AVIF instead of JPEG, or attach them in two emails.',
                });
                return;
              }
              setAttachments((prev) => [...prev, ...all]);
              // 🚩 The modal is gone by now, so anything it wanted to say has to be said here.
              // "HEIC support is not installed on the server" is the one that matters operationally.
              if (notes.length) ui.toast({ kind: 'info', message: 'Some images were attached unchanged', detail: notes.join('\n') });
            }}
          />
        )}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />
        {linkEdit && (
          <LinkEditModal
            initialHref={linkEdit.href}
            initialTitle={linkEdit.title}
            onSave={saveLink}
            onCancel={() => setLinkEdit(null)}
          />
        )}
      </div>

      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 border-b border-bmail-border bg-bmail-surface px-4 py-2">
          {attachments.map((a, i) => (
            <div key={i} className="flex items-center gap-2 rounded-lg border border-bmail-border bg-bmail-navy px-3 py-1.5 text-xs text-bmail-text">
              <Paperclip size={12} className="shrink-0 text-bmail-text-secondary" />
              <span className="truncate max-w-[160px]">{a.filename}</span>
              <span className="text-bmail-text-secondary">({Math.round(a.size / 1024)} KB)</span>
              <button
                onClick={() => removeAttachment(i)}
                className="text-bmail-text-secondary hover:text-bmail-error"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex-1 overflow-y-auto">
        <EditorContent editor={editor} />
      </div>

      <div className="border-t border-bmail-border px-6 py-4">
        <button
          onClick={onSaveDraft}
          disabled={draftSaving || (isSending) || (!to && !subject && !body)}
          className="mr-3 inline-flex items-center gap-1.5 rounded-lg border border-bmail-border px-4 py-2.5 text-sm font-medium text-bmail-text hover:bg-bmail-surface-hover disabled:opacity-50 transition-colors"
        >
          {draftSaved && !draftSaving ? <Check size={16} className="text-bmail-success" /> : <FileEdit size={16} />}
          {draftSaving ? 'Saving…' : draftSaved ? 'Saved' : 'Save draft'}
        </button>
        <button
          onClick={onSend}
          disabled={isSending || !to || !subject}
          className="rounded-lg bg-bmail-goldfill px-6 py-2.5 font-semibold text-bmail-on-accent hover:bg-bmail-goldfill-hover disabled:opacity-50 transition-colors"
        >
          {isSending ? 'Sending…' : 'Send'}
        </button>
      </div>
    </div>
  );
}
